import React from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiCheckCircle, FiTrendingUp, FiStar, FiGlobe } from 'react-icons/fi';
import Hero from '../../components/sections/Hero';
import AuditForm from '../../components/sections/AuditForm';
import ProcessSection from '../../components/sections/ProcessSection';
import FAQSection from '../../components/sections/FAQSection';
import SEO from '../../components/common/SEO';
import SafeIcon from '../../components/common/SafeIcon';
import { servicesData } from '../../data/services';

const LocalSEO = () => {
  const data = servicesData['local-seo'];
  
  const pillars = [
    {
      title: "GBP Mastery",
      desc: "Total optimization of your Google Business Profile to capture the 'Near Me' search traffic.",
      icon: FiStar
    },
    {
      title: "Local Citations",
      desc: "Ensuring your business data is 100% consistent across Yelp, BBB, and local Alberta directories.",
      icon: FiCheckCircle
    },
    {
      title: "Hyper-Local Content",
      desc: "Pages designed specifically for neighborhoods like Beltline in Calgary or Strathcona in Edmonton.",
      icon: FiMapPin
    },
    {
      title: "Technical Authority",
      desc: "Schema markup and site speed optimization that tells Google exactly where you operate.",
      icon: FiGlobe
    }
  ];

  return (
    <>
      <SEO 
        title="Local SEO Services Alberta | Rank #1 in Google Maps" 
        description="Dominate local search in Calgary, Edmonton, and Red Deer. Our specialized Local SEO strategies focus on the Google Map Pack and high-intent 'near me' keywords."
      />
      
      <Hero 
        title="Dominate Your City's Map Pack" 
        subtitle="Rank for the keywords that actually drive revenue. We help Alberta home service businesses own their local search market."
      />

      {/* Strategic Pillars */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-orange-600 font-bold uppercase tracking-wider text-sm mb-4">Local Dominance</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">If You Aren't in the Top 3, You're Invisible.</h3>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                In Alberta, 82% of homeowners use Google Maps to find local services. If your business isn't appearing in the "Map Pack" (the top 3 results), you are losing thousands in potential monthly revenue to your competitors.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {pillars.map((pillar, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="shrink-0 w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                      <SafeIcon icon={pillar.icon} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{pillar.title}</h4>
                      <p className="text-slate-500 text-xs leading-relaxed">{pillar.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <div className="relative">
              <div className="absolute inset-0 bg-orange-600 rounded-3xl transform rotate-2"></div>
              <div className="relative bg-slate-900 p-8 rounded-3xl shadow-2xl">
                <div className="flex items-center gap-3 mb-8 border-b border-slate-800 pb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-slate-500 text-xs ml-2">Google Map Pack Simulation</span>
                </div>
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`p-4 rounded-xl border ${i === 1 ? 'bg-white border-orange-500 shadow-lg' : 'bg-slate-800 border-slate-700'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className={`font-bold ${i === 1 ? 'text-slate-900' : 'text-white'}`}>
                            {i === 1 ? 'Your Business Name' : `Competitor ${i}`}
                          </p>
                          <div className="flex items-center gap-1 text-orange-400 text-xs my-1">
                            <SafeIcon icon={FiStar} /> <SafeIcon icon={FiStar} /> <SafeIcon icon={FiStar} /> <SafeIcon icon={FiStar} /> <SafeIcon icon={FiStar} />
                            <span className="ml-1 text-slate-400">4.9 (120+ reviews)</span>
                          </div>
                          <p className="text-xs text-slate-500">Open 24 hours • Calgary, AB</p>
                        </div>
                        {i === 1 && <span className="bg-orange-100 text-orange-600 text-[10px] font-black px-2 py-0.5 rounded">AD / FEATURED</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection />

      {/* Trust Section */}
      <section className="py-20 bg-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Own Your Local Market?</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-12 text-lg">
            Stop letting your competitors take the best leads in Alberta. Get a free, detailed analysis of your local search presence today.
          </p>
          <div className="max-w-xl mx-auto text-left">
            <AuditForm 
              title="Get Your Free Map Pack Audit" 
              subtitle="We'll show you exactly what's blocking you from the top 3 spots."
              buttonText="Analyze My Local Presence"
            />
          </div>
        </div>
      </section>

      <FAQSection />
    </>
  );
};

export default LocalSEO;